import {
  extractAxiosErrorMessage,
  OutreachPromptTestResponse,
  PromptAddendumModel,
  useOutreach,
  useOutreachTrace,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";

// The outreach-email prompt's test panel: unlike the landlord / people tests (which
// kick off a worker), drafting is one synchronous call, so this drafts the email
// inline against a real promoted lead (its first enriched contact) and renders the
// result + AI trace. Nothing is persisted. Handed the live draft addendum by the
// shared editor.
const OutreachTestPanel: React.FC<{ addendum: PromptAddendumModel }> = ({
  addendum,
}) => {
  const { testOutreachPrompt } = useOutreach();

  const [subjectRef, setSubjectRef] = useState<string>("");
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] =
    useState<OutreachPromptTestResponse | null>(null);
  const [showTrace, setShowTrace] = useState<boolean>(false);

  const running = testOutreachPrompt.isLoading;

  const handleTest = async () => {
    setTestError(null);
    setTestResult(null);
    setShowTrace(false);
    try {
      const res = await testOutreachPrompt.mutateAsync({
        addendum,
        subjectRef: subjectRef.trim(),
      });
      setTestResult(res);
    } catch (e) {
      setTestError(
        (await extractAxiosErrorMessage(e)) ?? "Failed to run the test draft."
      );
    }
  };

  const traceQuery = useOutreachTrace(
    testResult?.traceSubjectRef ?? null,
    testResult?.traceRunId ?? null,
    showTrace
  );

  const complete = testResult?.status === "complete";

  return (
    <div>
      <strong>Test this prompt</strong>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
        Drafts an email with the current draft (saved rules plus any unsaved preamble
        edit) for a real promoted lead&apos;s first enriched contact. Nothing is
        saved. Costs a fraction of a cent per run.
      </div>
      <div
        style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <Form.Label style={{ fontSize: 12, marginBottom: 2 }}>
            Promoted lead id
          </Form.Label>
          <Form.Control
            size="sm"
            value={subjectRef}
            placeholder="lead id (UUID) with a pulled contact"
            onChange={(e) => setSubjectRef(e.target.value)}
          />
        </div>
        <Button
          size="sm"
          variant="outline-primary"
          disabled={running || !subjectRef.trim()}
          onClick={() => void handleTest()}
        >
          {running ? (
            <>
              <Spinner as="span" size="sm" animation="border" /> Drafting...
            </>
          ) : (
            "Test draft"
          )}
        </Button>
      </div>

      {testError && (
        <Alert variant="danger" style={{ marginTop: 8 }}>
          {testError}
        </Alert>
      )}

      {testResult && !complete && (
        <Alert variant="warning" style={{ marginTop: 8 }}>
          {testResult.status === "no_contact"
            ? "That lead has no enriched contact yet - pull contact info first, then test."
            : `The test could not run (status: ${testResult.status}).`}
        </Alert>
      )}

      {testResult && complete && (
        <div
          style={{
            marginTop: 10,
            border: "1px solid #eee",
            borderRadius: 4,
            padding: 10,
          }}
        >
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}
          >
            <strong style={{ fontSize: 13 }}>Draft</strong>
            {testResult.traceRunId && (
              <Button
                size="sm"
                variant="link"
                style={{ fontSize: 12, padding: 0 }}
                onClick={() => setShowTrace(true)}
              >
                View AI trace
              </Button>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#8a94a0" }}>SUBJECT</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            {testResult.subject}
          </div>
          <div style={{ fontSize: 11, color: "#8a94a0" }}>BODY</div>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
            {testResult.body}
          </div>
        </div>
      )}

      <Modal show={showTrace} onHide={() => setShowTrace(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            Test draft - AI trace
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {traceQuery.isLoading ? (
            <div style={{ fontSize: 12, color: "#888" }}>Loading trace...</div>
          ) : traceQuery.data ? (
            <AITraceViewer trace={traceQuery.data} />
          ) : (
            <div style={{ fontSize: 12, color: "#888" }}>
              No AI trace available for this run.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OutreachTestPanel;
